import { useCallback, type ReactNode } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericEventFeed } from './GenericEventFeed';
import { useAlertFeedCardConfig, useMergedAlertFeedEvents } from './hooks';
import { useAlertsData } from '@/features/EventsListLayout/hooks';
import { useOpenInNewWindow } from '@/hooks';
import { useWebSocketStatus } from '@/hooks/useWebSocket';
import { AppRoutes } from '@/constants/routes';
import { getAlertRuleCacheKey } from '@/constants/cache-keys';
import { EngineService } from '@/services/engine';
import { AlertRuleActionType, type FullAlertRule } from '@/types/alert';
import { LIVE_REFRESH_INTERVAL_MS } from './constants';
import type { FeedEmptyStateProps } from './components';
import type { AlertRuleEvent } from '@/types/event';

function alertsArePolled(
    alertRule: FullAlertRule | undefined,
    isSocketConnected: boolean,
): boolean {
    if (!alertRule?.active) return false;
    if (!isSocketConnected) return true;
    return !alertRule.actions.some(
        (action) => action.type === AlertRuleActionType.APP_NOTIFICATION,
    );
}

interface AlertRuleFeedProps {
    alertRuleId: string;
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

export function AlertRuleFeed({
    alertRuleId,
    titleContent,
    emptyState,
}: AlertRuleFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const navigate = useNavigate();
    const { openEvent } = useOpenInNewWindow();
    const cardConfig = useAlertFeedCardConfig();

    const { data: alertRule } = useSWR<FullAlertRule>(
        getAlertRuleCacheKey(alertRuleId),
        () => EngineService.getAlertRule(alertRuleId),
    );
    const isSocketConnected = useWebSocketStatus();
    const refreshInterval = alertsArePolled(alertRule, isSocketConnected)
        ? LIVE_REFRESH_INTERVAL_MS
        : 0;

    const { events, isLoading, isLoadingMore, hasMore, loadMore } =
        useAlertsData({ alertRuleId, pageKey: 'alerts', refreshInterval });

    const { itemKey } = cardConfig;
    const filterByRule = useCallback(
        (alert: AlertRuleEvent) => alert.alertRuleId === alertRuleId,
        [alertRuleId],
    );
    const feedEvents = useMergedAlertFeedEvents(events, itemKey, filterByRule);

    return (
        <GenericEventFeed
            {...cardConfig}
            containerTitle={t('sidebar.recentAlerts')}
            titleContent={titleContent}
            onViewAll={() =>
                navigate(`${AppRoutes.ALERTS}?alertRuleId=${alertRuleId}`)
            }
            items={feedEvents}
            isLoading={isLoading}
            emptyState={emptyState}
            onNavigate={(alert, clickEvent) =>
                openEvent(alert.event.id, clickEvent, {
                    state: {
                        feedContext: { type: 'alertRule', alertRuleId },
                    },
                })
            }
            quickViewContextType="event-feed"
            pagination={{ hasMore, isLoadingMore, onLoadMore: loadMore }}
        />
    );
}
